import { ApolloCache } from '@apollo/client';
import {
  ListAIInferenceStreamsMetadata,
  ListAIInferenceStreamsMetadataDocument,
} from '../graphql/listAiInferenceStreamsMetadata.generated';

export const removeAIInferenceStreamFromCache = (cache: ApolloCache<any>, streamId: string) => {
  try {
    // First, try to evict the specific stream from cache
    const streamRef = cache.identify({ __typename: 'AIInferenceStream', streamId });
    if (streamRef) {
      cache.evict({ id: streamRef });
    }

    // Also invalidate any child streams queries
    cache.evict({
      fieldName: 'aiInferenceStreamsMetadata',
      args: { input: { parentID: streamId } },
    });

    cache.gc();
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn('Failed to remove AI inference stream from cache:', error);
  }
};

export const addAIInferenceStreamToCache = (
  cache: ApolloCache<any>,
  alias: string,
  newStream: ListAIInferenceStreamsMetadata['aiInferenceStreamsMetadata']['edges'][0]['node'],
) => {
  try {
    cache.updateQuery(
      {
        query: ListAIInferenceStreamsMetadataDocument,
        variables: {
          input: { alias },
        },
      },
      (prevData: ListAIInferenceStreamsMetadata) => ({
        ...prevData,
        aiInferenceStreamsMetadata: {
          ...prevData?.aiInferenceStreamsMetadata,
          edges: [{ node: newStream }, ...(prevData?.aiInferenceStreamsMetadata?.edges || [])],
        },
      }),
    );
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn('Failed to add AI inference stream to cache:', error);
  }
};

export const handleDrilldownCacheInvalidation = (
  cache: ApolloCache<any>,
  parentStreamId: string,
  newChildStreamId?: string,
) => {
  try {
    // When a drilldown creates a new child stream, we need to:
    // 1. Invalidate the parent stream's children query to show the new child
    // 2. Optionally invalidate the root query if the parent was a root stream

    // Invalidate queries that show children of the parent stream
    cache.evict({
      fieldName: 'aiInferenceStreamsMetadata',
      args: { input: { parentID: parentStreamId } },
    });

    // If we know the new child stream ID, we can be more specific
    if (newChildStreamId) {
      // The new child stream should be added to the cache automatically by the mutation response
      // But we may need to invalidate related queries to ensure consistency

      // If the parent stream was a root stream, also invalidate the root query
      // to ensure the parent appears in the correct position
      cache.evict({
        fieldName: 'aiInferenceStreamsMetadata',
        args: { input: { parentID: '' } },
      });
    }

    cache.gc();
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn('Failed to handle drilldown cache invalidation:', error);
  }
};
